<template>
    <div v-if="!settingsLoading" class="request-store__content" :key="displayType">
        <ModuleColumnBlock class="request__tools">
            <BaseTools
                :filters="filters"
                :show-export-button="canExportToExcel"
                :is-exporting="isExporting"
                :stats="stats"
                :visible-statuses="visibleStatuses"
                :show-overdue="showOverdue"
                :scope-counts="scopeCounts"
                :show-quick-filters="showQuickFilters || filters.state.method === 'inbox'"
                :hide-sort="displayRequestTypeIsTable"
                :show-global-search="['inbox', 'outbox', 'archive', 'store'].includes(filters.state.method)"
                @export="exportToExcel"
            ></BaseTools>

            <div class="request-store__view-tools" @click.stop.prevent>
                <div class="display-switch">
                    <button
                        type="button"
                        class="display-switch__btn"
                        :class="{ 'display-switch__btn_on': displayRequestTypeIsTable }"
                        @click="setDisplayType(DisplayType.TABLE)"
                    >
                        <span class="display-switch__icon">▦</span>
                        Таблица
                    </button>
                    <button
                        type="button"
                        class="display-switch__btn"
                        :class="{ 'display-switch__btn_on': !displayRequestTypeIsTable }"
                        @click="setDisplayType(DisplayType.CARD)"
                    >
                        <span class="display-switch__icon">☰</span>
                        Карточки
                    </button>
                </div>

                <div class="request-store__columns">
                    <button type="button" class="request-store__columns-btn" @click="toggleColumnsMenu">
                        <SettingsSvg class="request-store__columns-icon"></SettingsSvg>
                        {{ displayRequestTypeIsTable ? 'Колонки' : 'Отображение' }}
                    </button>

                    <ColumnSettingsMenu
                        class="request-store__columns-menu"
                        v-if="isColumnsMenuOpen"
                        :config="columnsConfig"
                        :show-columns-list="displayRequestTypeIsTable"
                        :text-lines="textLines"
                        :text-column-source="textColumnSource"
                        @save="applyColumnsConfig"
                        @update-text-lines="setTextLines"
                        @update-text-column-source="setTextColumnSource"
                    ></ColumnSettingsMenu>
                </div>
            </div>
        </ModuleColumnBlock>

        <VScroll class="request__scroll">
            <template v-if="displayRequestTypeIsTable">
                <RequestTableHead
                    :columns="visibleTableColumns"
                    :sort="filters.state.sort"
                    @sort="filters.setSort($event)"
                    @reorder="onHeaderReorder"
                ></RequestTableHead>
                <RequestStorePagination
                    v-if="perPage"
                    ref="pagination"
                    :endpoint="endpoint"
                    :query="activeQuery"
                    :per-page="perPage"
                    :style="{ minWidth: tableMinWidth }"
                    @loaded="onLoadedRequests"
                    @refreshed="loadStats"
                    @per-page="onPerPageChange"
                    v-model="page"
                >
                    <template v-slot="{ request }">
                        <RouterLink :to="getRequestLink(request)" class="router-link_disarm prevent-close">
                            <RequestTableRow
                                :columns="visibleTableColumns"
                                :request="request"
                                :text-lines="textLines"
                                :text-column-source="textColumnSource"
                            ></RequestTableRow>
                        </RouterLink>
                    </template>
                </RequestStorePagination>
            </template>

            <template v-else>
                <RequestStorePagination
                    v-if="perPage"
                    ref="pagination"
                    :endpoint="endpoint"
                    :query="activeQuery"
                    :per-page="perPage"
                    @loaded="onLoadedRequests"
                    @refreshed="loadStats"
                    @per-page="onPerPageChange"
                    v-model="page"
                >
                    <template v-slot="{ request }">
                        <RequestCard
                            :request="request"
                            :text-lines="textLines"
                            :text-column-source="textColumnSource"
                            class="prevent-close"
                        ></RequestCard>
                    </template>
                </RequestStorePagination>
            </template>
        </VScroll>

        <!-- Вне VScroll: внутри него футер уезжал вбок вместе с таблицей и вниз вместе
             со списком. Данные приходят из RequestStorePagination событием loaded. -->
        <RequestStoreFooter
            v-if="listTotal > 0"
            :total="listTotal"
            :total-pages="listTotalPages"
            :per-page="perPage"
            v-model="page"
            @per-page="onPerPageChange"
        ></RequestStoreFooter>
    </div>
</template>
<script>
import Vue from 'vue';
import http from '../../../js/http';
import axios from 'axios';
import debounce from 'lodash/debounce';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import userSettings from '@/js/userSettings';
import { createRequestFilters, buildPayload, buildQuerySignature } from '@/js/requestFilters';
import { canExportRequests } from '@/js/requestExport';

import RequestStoreDisplayType from '@/js/enums/RequestStoreDisplayType';
import RequestStoreDisplayTitle from '@/js/enums/RequestStoreDisplayTitle';
import RequestSortType from '@/js/enums/RequestSortType';

import ModuleColumn from '@/views/modules/default/ModuleColumn';
import ModuleColumnBlock from '@/views/modules/default/ModuleColumnBlock';
import ModuleColumnScroll from '@/views/modules/default/ModuleColumnScroll';
import ModuleColumnScrollInner from '@/views/modules/default/ModuleColumnScrollInner';
import ModuleColumnScrollWrapper from '@/views/modules/default/ModuleColumnScrollWrapper';

import VScroll from '@/views/components/VScroll/VScroll.vue';

import SettingsSvg from '@/images/settings.svg';
import VForm, { VFormField } from '@/views/components/VForm';
import BaseTools from '@/views/components/tools/BaseTools';
import VButton from '@/views/components/VButton/VButton.vue';
import ColumnLayer from '@/views/components/modules/ColumnLayer';
import ColumnLayerInner from '@/views/components/modules/ColumnLayerInner';
import { RequestTableHead, RequestTableRow } from '@/views/components/RequestTable';
import RequestCard from '@/views/components/RequestCard';
import ColumnSettingsMenu from '@/views/components/RequestTable/ColumnSettingsMenu.vue';
import { normalizeConfig, serializeConfig, visibleColumns, defaultConfig, pinnedFirst } from '@/js/tableColumns';
import { gridMinWidthCss } from '@/views/components/RequestTable/requestRowData';
import RequestStorePagination from '@/views/components/RequestStorePagination';
import RequestStoreFooter from '@/views/components/RequestStorePagination/RequestStoreFooter.vue';

import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';
import RequestView from './RequestView.vue';

const options = Object.keys(RequestStoreDisplayTitle).map((key) => ({
    value: parseInt(key),
    label: RequestStoreDisplayTitle[key],
}));
// Тот же порог, что у адаптивной раскладки панелей в App.vue ($media-md).
const adaptiveQuery = window.matchMedia('(max-width: 800px)');

const Account = namespace('account');
const Workspace = namespace('workspace');

@Component({
    components: {
        ModuleColumn,
        ModuleColumnBlock,
        ModuleColumnScroll,
        ModuleColumnScrollInner,
        ModuleColumnScrollWrapper,
        VScroll,

        VForm,
        VFormField,
        BaseTools,
        VButton,
        ColumnLayer,
        ColumnLayerInner,
        RequestStorePagination,
        RequestStoreFooter,
        RequestTableHead,
        RequestTableRow,
        RequestCard,
        ColumnSettingsMenu,
        SettingsSvg,
        RequestView,

        Splitpanes,
        Pane,
    },
})
class RequestStore extends Vue {
    @Account.State account;
    @Account.Mutation REQUEST_STORE_SETTINGS_UPDATE;

    @Workspace.Mutation SET_REQUESTS_CONTEXT;
    @Workspace.Mutation UPDATE_MENU_INFO;
    @Workspace.State requestPermissions;

    page = 1;
    isAdaptive = adaptiveQuery.matches;
    listTotal = 0;
    listTotalPages = 1;
    filters = createRequestFilters();
    options = options;
    displayType = RequestStoreDisplayType.CARD;
    isExporting = false;
    perPage = 15;
    settingsLoading = true;
    stats = null;
    visibleStatuses = ['0', '1', '2', '3', '4', '5'];
    showOverdue = true;
    showQuickFilters = false;
    statsAbortController = null;
    activeQuery = null;
    columnsConfig = defaultConfig();
    isColumnsMenuOpen = false;
    textLines = 1;
    // 'description' — то, что просили исполнители: текст самого обращения, а не
    // последний ответ. Люди, которые ничего не трогали, не должны увидеть разницы.
    textColumnSource = 'description';

    propagateQueryDebounced = debounce(
        function () {
            this.activeQuery = this.query;
            this.loadStats();
        },
        350,
        { leading: true, trailing: true },
    );

    get query() {
        if (!this.filters.state.ready) return null;

        return {
            search: this.filters.state.search,
            sort: this.filters.state.sort,
            filter: buildPayload(this.filters.state),
        };
    }

    get querySignature() {
        // method — из filters.state, не из $route.params.method напрямую: см. подробное
        // обоснование в buildQuerySignature (frontend/src/js/requestFilters/querySignature.js) —
        // там же объяснена регрессия, которую эта развязка чинит.
        return buildQuerySignature({
            method: this.filters.state.method,
            id: this.$route.params.id,
            query: this.query,
        });
    }

    @Watch('querySignature')
    onQueryChange(signature) {
        if (!signature) return;
        this.propagateQueryDebounced();
    }

    async loadStats() {
        if (!this.activeQuery) return;

        if (this.statsAbortController) {
            this.statsAbortController.abort();
        }
        this.statsAbortController = new AbortController();

        try {
            const { data } = await http.post(
                '/requests/stats',
                {
                    ...this.activeQuery,
                    method: this.$route.params.method,
                },
                { signal: this.statsAbortController.signal },
            );
            this.stats = data;
        } catch (e) {
            if (axios.isCancel?.(e) || e.code === 'ERR_CANCELED') return;
            console.warn(e);
        }
    }

    async exportToExcel() {
        if (!this.canExportToExcel || this.isExporting) {
            return;
        }
        if (!this.query) return;

        this.isExporting = true;
        this.$bus.emit('export:progress:start');

        try {
            const response = await http.post(
                '/requests/store/export',
                {
                    search: this.query.search || '',
                    sort: this.query.sort || {
                        type: RequestSortType.DATE,
                        reverse: true,
                    },
                    filter: this.query.filter || {},
                },
                {
                    responseType: 'blob',
                },
            );
            this.downloadExportFile(response);
            this.$bus.emit('export:progress:success');
        } catch (error) {
            this.$bus.emit('export:progress:error', await this.extractExportErrorMessage(error));
        } finally {
            this.isExporting = false;
        }
    }

    async extractExportErrorMessage(error) {
        const fallback = 'Не удалось сформировать файл. Попробуйте позже.';
        const data = error && error.response ? error.response.data : null;

        // responseType 'blob' means an error body arrives as a Blob too, so read and parse it to get
        // the server-side message (Laravel returns JSON like { "message": "..." }).
        if (data instanceof Blob) {
            try {
                const parsed = JSON.parse(await data.text());
                return parsed.message || fallback;
            } catch (e) {
                return fallback;
            }
        }

        return (data && data.message) || fallback;
    }
    downloadExportFile(response) {
        const fileName = this.getExportFileName(response.headers['content-disposition']);
        const blob = new Blob([response.data], {
            type:
                response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }

    getExportFileName(contentDisposition) {
        if (!contentDisposition) {
            return 'requests-export.xlsx';
        }

        const utfName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utfName && utfName[1]) {
            return decodeURIComponent(utfName[1]);
        }

        const fallbackName = contentDisposition.match(/filename="?([^";]+)"?/i);
        if (fallbackName && fallbackName[1]) {
            return fallbackName[1];
        }

        return 'requests-export.xlsx';
    }
    onLoadedRequests({ requests, total, totalPages }) {
        this.listTotal = total;
        this.listTotalPages = totalPages;
        this.SET_REQUESTS_CONTEXT(requests);
    }
    onRequestClaimed() {
        this.$refs.pagination?.refresh();
        this.loadStats();
        http.post('/requests/info')
            .then(({ data }) => this.UPDATE_MENU_INFO(data))
            .catch(console.warn);
    }
    onPerPageChange(value) {
        if (this.perPage === value) return;
        this.perPage = value;
        http.post('/account-settings/update', {
            settings: [{ key: 'request-store.per-page', value, type: 'integer' }],
        })
            .then(() => userSettings.clearCache())
            .catch(console.error);
    }
    getRequestLink(request) {
        return {
            name: 'request',
            params: {
                id: request.id,
                method: this.$route.params.method,
            },
        };
    }
    setDisplayType(value) {
        if (this.displayType === value) return;
        this.displayType = value;
        this.isColumnsMenuOpen = false;
        http.post('/account-settings/update', {
            settings: [{ key: 'request-store.display-type', value, type: 'integer' }],
        })
            .then(() => userSettings.clearCache())
            .catch(console.error);
    }
    // В отличие от setDisplayType() панель не закрывает — она открыта ради настройки
    // колонок/строк текста, один клик по сегменту не повод её захлопывать.
    setTextLines(value) {
        if (this.textLines === value) return;
        this.textLines = value;
        http.post('/account-settings/update', {
            settings: [{ key: 'request-store.text-lines', value, type: 'integer' }],
        })
            .then(() => userSettings.clearCache())
            .catch(console.error);
    }
    setTextColumnSource(value) {
        if (this.textColumnSource === value) return;
        this.textColumnSource = value;
        http.post('/account-settings/update', {
            settings: [{ key: 'request-store.text-column-source', value, type: 'string' }],
        })
            .then(() => userSettings.clearCache())
            .catch(console.error);
    }
    toggleColumnsMenu() {
        this.isColumnsMenuOpen = !this.isColumnsMenuOpen;
    }
    hideColumnsMenu() {
        this.isColumnsMenuOpen = false;
    }
    applyColumnsConfig(rawConfig) {
        // Единственная точка сохранения порядка — и из меню, и из перетаскивания
        // заголовков, поэтому закреплённые колонки поднимаем здесь, а не в каждой ветке.
        const config = pinnedFirst(rawConfig);

        this.columnsConfig = config;
        this.isColumnsMenuOpen = false;
        http.post('/account-settings/update', {
            settings: [{ key: 'request-store.table-columns', value: serializeConfig(config), type: 'string' }],
        })
            .then(() => userSettings.clearCache())
            .catch(console.error);
    }
    onHeaderReorder(keys) {
        const visible = keys.map((key) => this.columnsConfig.find((item) => item.key === key));
        const hidden = this.columnsConfig.filter((item) => !keys.includes(item.key));
        this.applyColumnsConfig([...visible, ...hidden]);
    }
    get endpoint() {
        const id = this.$route.params.id;
        const method = this.$route.params.method;

        if (method === 'view') return `/requests/${method}/${id}`;

        if (!isNaN(method)) return `/requests/group/${method}`;

        return `/requests/${method}`;
    }

    get scopeCounts() {
        return this.stats && this.stats.scopes ? this.stats.scopes : null;
    }

    get displayRequestTypeIsTable() {
        return this.displayType === RequestStoreDisplayType.TABLE;
    }

    get DisplayType() {
        return RequestStoreDisplayType;
    }

    get visibleTableColumns() {
        // Заголовок «Описание» переопределяется здесь же, где колонки готовятся для
        // шапки/строк/смещений — единая точка, вместо правки заголовка в каждом
        // потребителе по отдельности. Реестр COLUMNS про эту настройку не знает.
        const columns = visibleColumns(this.columnsConfig).map((column) =>
            column.key === 'description' ? { ...column, title: this.textColumnTitle } : column,
        );

        // На телефоне закрепление не применяем: закреплённая группа заняла бы почти
        // всю ширину экрана, и прокручивать было бы нечего. Настройку при этом не
        // трогаем — на большом экране у того же пользователя она продолжит работать.
        // Одна точка на всё: отсюда колонки берут и шапка, и строки, и смещения.
        return this.isAdaptive ? columns.map((column) => ({ ...column, pinned: false })) : columns;
    }

    get textColumnTitle() {
        return this.textColumnSource === 'last-message' ? 'Последнее сообщение' : 'Описание';
    }

    // Блок списка тянется на всю ширину сетки, как шапка и строки: иначе он остаётся
    // шириной видимой области, и строки торчат из своего же контейнера.
    get tableMinWidth() {
        return gridMinWidthCss(this.visibleTableColumns);
    }

    get canExportToExcel() {
        return canExportRequests(this.requestPermissions, this.$route.params.method);
    }

    get requestStoreSettings() {
        return this.account && this.account.requestStoreSettings
            ? this.account.requestStoreSettings
            : {
                  displayType: RequestStoreDisplayType.CARD,
              };
    }
    @Watch('$route.params.method')
    f(newMethod) {
        // Вотчер querySignature объявлен раньше и успевает сработать до filters.init —
        // тогда он ловит новый route.method при ещё старом query. cancel() сбрасывает окно
        // дебаунса, чтобы повторный вызов после init отработал как leading, а не trailing.
        this.propagateQueryDebounced.cancel();
        this.$bus.emit('tools:clear');
        this.filters.init(newMethod);
    }
    async loadSettings() {
        try {
            const savedDisplayType = await userSettings.getDisplayTypeAsync();
            this.perPage = Number(await userSettings.getAsync('request-store.per-page')) || 15;

            const savedStatuses = await userSettings.getAsync('request-store.stats-visible-statuses');
            this.visibleStatuses = savedStatuses ? JSON.parse(savedStatuses) : ['0', '1', '2', '3', '4', '5'];

            this.showOverdue = await userSettings.getAsync('request-store.stats-show-overdue', true);
            this.showQuickFilters = await userSettings.getAsync('request-store.show-quick-filters', false);

            this.columnsConfig = normalizeConfig(await userSettings.getAsync('request-store.table-columns'));
            this.textLines = Number(await userSettings.getAsync('request-store.text-lines')) || 1;
            this.textColumnSource = await userSettings.getAsync('request-store.text-column-source', 'description');

            if (savedDisplayType !== null) {
                let parsed = parseInt(savedDisplayType, 10);
                // display-type=3 — легаси-значение "Табличный с описанием" из сохранённых настроек
                // пользователя; такого вида больше нет, поэтому при чтении маппим его в TABLE.
                if (parsed === 3) parsed = RequestStoreDisplayType.TABLE;
                this.displayType = parsed;
            }

            this.settingsLoading = false;
        } catch (e) {
            console.warn(e);
            this.settingsLoading = false;
        }
    }
    async mounted() {
        await this.loadSettings();
        this.$bus.on('settings:updated', this.loadSettings);
        this.$bus.on('request:created', this.loadStats);
        this.$bus.on('request:status-changed', this.loadStats);
        this.$bus.on('request:claimed', this.onRequestClaimed);
        document.addEventListener('click', this.hideColumnsMenu);
        adaptiveQuery.addEventListener('change', this.onAdaptiveChange);

        this.filters.init(this.$route.params.method);
    }
    onAdaptiveChange() {
        this.isAdaptive = adaptiveQuery.matches;
    }
    beforeDestroy() {
        this.propagateQueryDebounced.cancel();
        this.$bus.off('settings:updated', this.loadSettings);
        this.$bus.off('request:created', this.loadStats);
        this.$bus.off('request:status-changed', this.loadStats);
        this.$bus.off('request:claimed', this.onRequestClaimed);
        document.removeEventListener('click', this.hideColumnsMenu);
        adaptiveQuery.removeEventListener('change', this.onAdaptiveChange);
    }
}

export default RequestStore;
</script>
<style lang="scss">
.request-store__content {
    width: 100%;
    height: 100%;
    padding: 20px 0 0 0;
    position: relative;
    // Колонка: блок инструментов занимает свою высоту, скролл забирает остаток.
    // Без flex .ps с height: 100% уходил ниже видимой области ровно на высоту
    // инструментов, и футер со счётчиком и пейджером был недоступен прокруткой.
    display: flex;
    flex-direction: column;

    .v-scroll {
        // Только снизу. Горизонтальный отступ здесь недопустим: overflow обрезает
        // по padding-box, и уехавшие колонки просвечивали бы в этом просвете слева
        // от закреплённых. Панель меню отделена от таблицы слоем, а не отступом.
        padding-bottom: 20px;
    }
}
.request {
    &__actions {
        width: 100%;
        display: flex;
        position: absolute;
        padding: 0 10px;
        left: 0;
        bottom: 0;
        justify-content: flex-end;
    }

    &__tools {
        max-height: 100%;
    }

    &__scroll {
        flex: 1 1 auto;
        min-height: 0;
        height: auto;
        width: auto;
    }

    &__table-header {
        margin: 0 -10px;
    }
}

.request-store {
    // Перенос — для узкой панели и крупного текста: кнопка «Колонки» уходит на вторую
    // строку, а не за правый край.
    &__view-tools {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
        padding-top: 4px;
        padding-bottom: 12px;
    }

    &__columns {
        position: relative;
    }

    &__columns-icon {
        width: 15px;
        height: 15px;
        flex-shrink: 0;
    }

    &__columns-btn {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 34px;
        padding: 0 14px;
        border-radius: 999px;
        border: 1px solid #e3e5e8;
        background: var(--color-white);
        color: #3c4043;
        font: 500 0.8125rem var(--font-family);
        cursor: pointer;
        white-space: nowrap;

        &:hover {
            background: #f6f7f8;
            border-color: #d4d7db;
        }
    }

    &__columns-menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        z-index: $layer-dropdown;
    }
}

.display-switch {
    display: flex;
    gap: 2px;
    padding: 3px;
    border: 1px solid #e3e5e8;
    border-radius: 11px;
    background: var(--color-white);

    &__btn {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: 0;
        background: transparent;
        border-radius: 8px;
        padding: 6px 13px;
        font: 500 0.8125rem var(--font-family);
        color: #5f6368;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
            background: #f6f7f8;
            color: #3c4043;
        }

        &_on {
            background: #e8f5e9;
            color: #2e7d32;
            font-weight: 600;

            &:hover {
                background: #e8f5e9;
            }
        }
    }

    &__icon {
        font-size: 14px;
    }
}
</style>
